import React, { useState, useMemo } from 'react';
import * as XLSX from 'xlsx';

// --- IMPORTACIÓN DE NUESTRAS UTILIDADES DE SEGUROS ---
import { parseCurrency, formatCurrencyUniversal } from '../../../../utils/segurosUtils';

// --- ICONOS SVG LOCALES ---
const IconUser = () => (
    <svg className="w-5 h-5 text-indigo-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
);

const IconFolder = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
    </svg>
);

const IconBack = () => (
    <svg className="w-4 h-4 transition-transform group-hover:-translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
    </svg>
);

const IconDownload = () => (
    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
    </svg>
);

// ESTADOS PARA CUOTAS PENDIENTES
const ESTADOS_PENDIENTES = [
    'SIN ASIGNAR',
    'PENDIENTE',
    'INFORMADA',
    'RECLAMADA',
    'CBU READHERIDO',
    'TC READHERIDO'
];

/**
 * Escalonamiento objetivo por cuotas efectivamente cobradas:
 * - Hasta 50 cuotas: 0.4%
 * - De 51 a 120 cuotas: 0.8%
 * - Más de 120 cuotas: 1.0%
 */
const calcularPorcentajePorVolumen = (cuotasCobradas) => {
    if (cuotasCobradas > 250) return 1.0;
    if (cuotasCobradas >= 150) return 0.8;
    return 0.4;
};

const RendimientoCobradores = ({ registros, isDbLoaded }) => {
    const [selectedCobrador, setSelectedCobrador] = useState('');
    const [subTab, setSubTab] = useState('COBRADAS'); // COBRADAS | DEBITADAS | PENDIENTES | ANULADAS

    // --- HELPERS DE RESPONSABILIDAD ---
    const getCobrador = (item) => {
        const val = item.quienCobro;
        return (val && String(val).trim() !== '' && String(val).trim() !== 'null') 
            ? String(val).trim().toUpperCase() 
            : 'SIN ASIGNAR';
    };

    const getInformador = (item) => {
        const val = item.quienInformo;
        return (val && String(val).trim() !== '' && String(val).trim() !== 'null') 
            ? String(val).trim().toUpperCase() 
            : 'SIN ASIGNAR';
    };

    // --- 1. MATRIZ DE RANKING GENERAL DE COBRADORES ---
    const matrizCobradores = useMemo(() => {
        if (!isDbLoaded || !registros) return [];

        const mapCobradores = {};

        const getObj = (nombre) => {
            if (!mapCobradores[nombre]) {
                mapCobradores[nombre] = {
                    nombre,
                    cuotasCobradas: 0,
                    cuotasDebitadas: 0,
                    cuotasPendientes: 0,
                    cuotasAnuladas: 0,
                    cobradoARS: 0,
                    cobradoUSD: 0,
                    debitadoARS: 0,
                    debitadoUSD: 0,
                    pendienteARS: 0,
                    pendienteUSD: 0,
                    anuladoARS: 0,
                    anuladoUSD: 0
                };
            }
            return mapCobradores[nombre];
        };

        registros.forEach(item => {
            const estado = String(item.estadoGestion || item.estado || '').trim().toUpperCase();
            const importe = parseCurrency(item.importe);
            const monedaRaw = String(item.moneda || item.monedaCobranza || item.monedaRefacturacion || '$').toUpperCase().trim();
            const esUSD = monedaRaw.includes('USD') || monedaRaw.includes('U$S') || monedaRaw.includes('DOLAR');

            // 1. COBRADA + ACREDITADA -> Busca en quienCobro
            if (['COBRADA', 'ACREDITADA'].includes(estado)) {
                const cobradorObj = getObj(getCobrador(item));
                cobradorObj.cuotasCobradas += 1;
                if (esUSD) cobradorObj.cobradoUSD += importe;
                else cobradorObj.cobradoARS += importe;
            }

            // 2. DEBITADA -> Busca en quienCobro
            else if (estado === 'DEBITADA') {
                const cobradorObj = getObj(getCobrador(item));
                cobradorObj.cuotasDebitadas += 1;
                if (esUSD) cobradorObj.debitadoUSD += importe;
                else cobradorObj.debitadoARS += importe;
            }

            // 3. PENDIENTES -> Busca en quienInformo
            else if (ESTADOS_PENDIENTES.includes(estado)) {
                const informadorObj = getObj(getInformador(item));
                informadorObj.cuotasPendientes += 1;
                if (esUSD) informadorObj.pendienteUSD += importe;
                else informadorObj.pendienteARS += importe;
            }

            // 4. ANULADAS -> Busca en quienInformo
            else if (estado === 'ANULADA') {
                const informadorObj = getObj(getInformador(item));
                informadorObj.cuotasAnuladas += 1;
                if (esUSD) informadorObj.anuladoUSD += importe;
                else informadorObj.anuladoARS += importe;
            }
        });

        return Object.values(mapCobradores).map(cob => {
            // El volumen total penaliza con pendientes y anuladas en el denominador
            const totalVolumen = cob.cuotasCobradas + cob.cuotasDebitadas + cob.cuotasPendientes + cob.cuotasAnuladas;
            const efectividad = totalVolumen > 0 
                ? ((cob.cuotasCobradas + cob.cuotasDebitadas) / totalVolumen) * 100 
                : 0;

            const pctIncentivo = calcularPorcentajePorVolumen(cob.cuotasCobradas);
            const incentivoARS = (cob.cobradoARS * pctIncentivo) / 100;
            const incentivoUSD = (cob.cobradoUSD * pctIncentivo) / 100;

            return {
                ...cob,
                efectividad,
                pctIncentivo,
                incentivoARS,
                incentivoUSD
            };
        }).sort((a, b) => b.cuotasCobradas - a.cuotasCobradas);
    }, [registros, isDbLoaded]);

    // --- 2. DETALLE INDIVIDUAL DEL COLABORADOR SELECCIONADO ---
    const reporteIndividual = useMemo(() => {
        if (!isDbLoaded || !selectedCobrador || !registros) {
            return {
                cuotasCobradas: 0,
                cuotasDebitadas: 0,
                cuotasPendientes: 0,
                cuotasAnuladas: 0,
                cobradoARS: 0,
                cobradoUSD: 0,
                debitadoARS: 0,
                debitadoUSD: 0,
                pendienteARS: 0,
                pendienteUSD: 0,
                anuladoARS: 0,
                anuladoUSD: 0,
                efectividad: 0,
                pctIncentivo: 0.4,
                incentivoARS: 0,
                incentivoUSD: 0,
                itemsCobradas: [],
                itemsDebitadas: [],
                itemsPendientes: [],
                itemsAnuladas: []
            };
        }

        const itemsCobradas = registros.filter(r => {
            const estado = String(r.estadoGestion || r.estado || '').trim().toUpperCase();
            return ['COBRADA', 'ACREDITADA'].includes(estado) && getCobrador(r) === selectedCobrador;
        });

        const itemsDebitadas = registros.filter(r => {
            const estado = String(r.estadoGestion || r.estado || '').trim().toUpperCase();
            return estado === 'DEBITADA' && getCobrador(r) === selectedCobrador;
        });

        const itemsPendientes = registros.filter(r => {
            const estado = String(r.estadoGestion || r.estado || '').trim().toUpperCase();
            return ESTADOS_PENDIENTES.includes(estado) && getInformador(r) === selectedCobrador;
        });

        const itemsAnuladas = registros.filter(r => {
            const estado = String(r.estadoGestion || r.estado || '').trim().toUpperCase();
            return estado === 'ANULADA' && getInformador(r) === selectedCobrador;
        });

        let cobradoARS = 0;
        let cobradoUSD = 0;
        let debitadoARS = 0;
        let debitadoUSD = 0;
        let pendienteARS = 0;
        let pendienteUSD = 0;
        let anuladoARS = 0;
        let anuladoUSD = 0;

        itemsCobradas.forEach(item => {
            const importe = parseCurrency(item.importe);
            const monedaRaw = String(item.moneda || item.monedaCobranza || item.monedaRefacturacion || '$').toUpperCase().trim();
            const esUSD = monedaRaw.includes('USD') || monedaRaw.includes('U$S') || monedaRaw.includes('DOLAR');
            if (esUSD) cobradoUSD += importe;
            else cobradoARS += importe;
        });

        itemsDebitadas.forEach(item => {
            const importe = parseCurrency(item.importe);
            const monedaRaw = String(item.moneda || item.monedaCobranza || item.monedaRefacturacion || '$').toUpperCase().trim();
            const esUSD = monedaRaw.includes('USD') || monedaRaw.includes('U$S') || monedaRaw.includes('DOLAR');
            if (esUSD) debitadoUSD += importe;
            else debitadoARS += importe;
        });

        itemsPendientes.forEach(item => {
            const importe = parseCurrency(item.importe);
            const monedaRaw = String(item.moneda || item.monedaCobranza || item.monedaRefacturacion || '$').toUpperCase().trim();
            const esUSD = monedaRaw.includes('USD') || monedaRaw.includes('U$S') || monedaRaw.includes('DOLAR');
            if (esUSD) pendienteUSD += importe;
            else pendienteARS += importe;
        });

        itemsAnuladas.forEach(item => {
            const importe = parseCurrency(item.importe);
            const monedaRaw = String(item.moneda || item.monedaCobranza || item.monedaRefacturacion || '$').toUpperCase().trim();
            const esUSD = monedaRaw.includes('USD') || monedaRaw.includes('U$S') || monedaRaw.includes('DOLAR');
            if (esUSD) anuladoUSD += importe;
            else anuladoARS += importe;
        });

        const totalVolumen = itemsCobradas.length + itemsDebitadas.length + itemsPendientes.length + itemsAnuladas.length;
        const efectividad = totalVolumen > 0 
            ? ((itemsCobradas.length + itemsDebitadas.length) / totalVolumen) * 100 
            : 0;

        const pctIncentivo = calcularPorcentajePorVolumen(itemsCobradas.length);
        const incentivoARS = (cobradoARS * pctIncentivo) / 100;
        const incentivoUSD = (cobradoUSD * pctIncentivo) / 100;

        return {
            cuotasCobradas: itemsCobradas.length,
            cuotasDebitadas: itemsDebitadas.length,
            cuotasPendientes: itemsPendientes.length,
            cuotasAnuladas: itemsAnuladas.length,
            cobradoARS,
            cobradoUSD,
            debitadoARS,
            debitadoUSD,
            pendienteARS,
            pendienteUSD,
            anuladoARS,
            anuladoUSD,
            efectividad,
            pctIncentivo,
            incentivoARS,
            incentivoUSD,
            itemsCobradas,
            itemsDebitadas,
            itemsPendientes,
            itemsAnuladas
        };
    }, [selectedCobrador, registros, isDbLoaded]);

    // --- 3. EXPORTAR A EXCEL ---
    const exportarExcelCobrador = (tipoPool) => {
        const poolExport = tipoPool === 'COBRADAS' ? reporteIndividual.itemsCobradas
                         : tipoPool === 'DEBITADAS' ? reporteIndividual.itemsDebitadas
                         : tipoPool === 'ANULADAS' ? reporteIndividual.itemsAnuladas
                         : reporteIndividual.itemsPendientes;

        if (!poolExport || poolExport.length === 0) return;

        let datosExcel = [];

        if (tipoPool === 'COBRADAS') {
            datosExcel = poolExport.map(item => {
                const estado = String(item.estadoGestion || item.estado || '').trim().toUpperCase();
                const importe = parseCurrency(item.importe);
                const monedaRaw = String(item.moneda || item.monedaCobranza || item.monedaRefacturacion || '$').toUpperCase().trim();
                const esUSD = monedaRaw.includes('USD') || monedaRaw.includes('U$S') || monedaRaw.includes('DOLAR');
                const incentivoItem = (importe * reporteIndividual.pctIncentivo) / 100;

                return {
                    'Asegurado': `${item.clienteNombre || ''} ${item.clienteApellido || ''}`.trim(),
                    'Póliza': item.nPoliza || '',
                    'Compañía': item.compania || item.companiaExpirante || '',
                    'Riesgo': item.riesgo || '',
                    'Cuota': item.cuota || '-',
                    'Endoso': item.endoso || '-',
                    'N° Recibo': item.nroRecibo || '-',
                    'Estado': estado,
                    'Moneda': esUSD ? 'USD' : 'ARS',
                    'Importe Cuota': importe,
                    'Forma de Pago': item.formaPago || '-',
                    'Quién Informó': item.quienInformo || '-',
                    'Quién Cobró': item.quienCobro || '-',
                    'Fecha Cobro': item.fechaCobro || '-',
                    'Incentivo': incentivoItem
                };
            });
        } else if (tipoPool === 'DEBITADAS') {
            datosExcel = poolExport.map(item => {
                const importe = parseCurrency(item.importe);
                const monedaRaw = String(item.moneda || item.monedaCobranza || item.monedaRefacturacion || '$').toUpperCase().trim();
                const esUSD = monedaRaw.includes('USD') || monedaRaw.includes('U$S') || monedaRaw.includes('DOLAR');

                return {
                    'Asegurado': `${item.clienteNombre || ''} ${item.clienteApellido || ''}`.trim(),
                    'Póliza': item.nPoliza || '',
                    'Compañía': item.compania || item.companiaExpirante || '',
                    'Riesgo': item.riesgo || '',
                    'Cuota': item.cuota || '-',
                    'Endoso': item.endoso || '-',
                    'N° Recibo': item.nroRecibo || '-',
                    'Estado': 'DEBITADA',
                    'Moneda': esUSD ? 'USD' : 'ARS',
                    'Importe Cuota': importe,
                    'Forma de Pago': item.formaPago || '-',
                    'Quién Informó': item.quienInformo || '-',
                    'Quién Cobró': item.quienCobro || '-',
                    'Fecha Cobro': item.fechaCobro || '-'
                };
            });
        } else if (tipoPool === 'ANULADAS') {
            datosExcel = poolExport.map(item => {
                const importe = parseCurrency(item.importe);
                const monedaRaw = String(item.moneda || item.monedaCobranza || item.monedaRefacturacion || '$').toUpperCase().trim();
                const esUSD = monedaRaw.includes('USD') || monedaRaw.includes('U$S') || monedaRaw.includes('DOLAR');

                return {
                    'Asegurado': `${item.clienteNombre || ''} ${item.clienteApellido || ''}`.trim(),
                    'Póliza': item.nPoliza || '',
                    'Compañía': item.compania || item.companiaExpirante || '',
                    'Riesgo': item.riesgo || '',
                    'Cuota': item.cuota || '-',
                    'Endoso': item.endoso || '-',
                    'N° Recibo': item.nroRecibo || '-',
                    'Estado': 'ANULADA',
                    'Moneda': esUSD ? 'USD' : 'ARS',
                    'Importe Cuota': importe,
                    'Forma de Pago': item.formaPago || '-',
                    'Quién Informó': item.quienInformo || '-',
                    'Fecha Carga': item.fechaCarga || '-'
                };
            });
        } else {
            datosExcel = poolExport.map(item => {
                const estado = String(item.estadoGestion || item.estado || '').trim().toUpperCase();
                const importe = parseCurrency(item.importe);
                const monedaRaw = String(item.moneda || item.monedaCobranza || item.monedaRefacturacion || '$').toUpperCase().trim();
                const esUSD = monedaRaw.includes('USD') || monedaRaw.includes('U$S') || monedaRaw.includes('DOLAR');

                return {
                    'Asegurado': `${item.clienteNombre || ''} ${item.clienteApellido || ''}`.trim(),
                    'Póliza': item.nPoliza || '',
                    'Compañía': item.compania || item.companiaExpirante || '',
                    'Riesgo': item.riesgo || '',
                    'Cuota': item.cuota || '-',
                    'Endoso': item.endoso || '-',
                    'Estado Deuda': item.estadoDeuda || '-',
                    'Intento Cobro': item.intentoCobro !== undefined && item.intentoCobro !== null && item.intentoCobro !== '' ? item.intentoCobro : '-',
                    'N° Recibo': item.nroRecibo || '-',
                    'Estado': estado,
                    'Moneda': esUSD ? 'USD' : 'ARS',
                    'Importe Cuota': importe,
                    'Forma de Pago': item.formaPago || '-',
                    'Quién Informó': item.quienInformo || '-',
                    'Fecha Reclamo': item.fechaReclamo || '-',
                    'Motivo Deuda': item.motivoDeuda || '-'
                };
            });
        }

        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(datosExcel);
        ws['!cols'] = Object.keys(datosExcel[0] || {}).map(() => ({ wch: 22 }));
        XLSX.utils.book_append_sheet(wb, ws, tipoPool);
        XLSX.writeFile(wb, `Cobranzas_${selectedCobrador}_${tipoPool}_${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

    if (!isDbLoaded) {
        return (
            <div className="p-12 text-center text-slate-400 italic text-sm bg-white rounded-3xl border border-slate-200/60 shadow-sm max-w-xl mx-auto mt-16 animate-fade-in">
                Cargando rendimiento de cobranzas por cobrador...
            </div>
        );
    }

    if (matrizCobradores.length === 0) {
        return (
            <div className="p-12 text-center text-slate-400 italic text-sm bg-white rounded-3xl border border-slate-200/60 shadow-sm max-w-xl mx-auto mt-16 animate-fade-in">
                No se encontraron registros de cobranza asociados en este período.
            </div>
        );
    }

    return (
        <div className="space-y-6 text-left animate-fade-in">
            
            {/* ESCENARIO A: RANKING GENERAL */}
            {!selectedCobrador ? (
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden text-left flex flex-col animate-fade-in">
                    <div className="p-5 px-6 border-b border-slate-100 flex justify-between items-center text-left bg-gradient-to-r from-slate-50 to-white">
                        <div className="flex items-center gap-3">
                            <span className="p-2.5 bg-indigo-50 text-indigo-600 rounded-xl shrink-0">
                                <IconUser />
                            </span>
                            <div>
                                <h4 className="text-sm font-black tracking-tight text-[#01032d] uppercase">Rendimiento de Cobranzas por Responsable</h4>
                                <p className="text-[10px] text-slate-400 font-bold mt-0.5 tracking-wider uppercase">
                                    Incentivo por cuotas cobradas: Hasta 50 (0.4%) • 51 a 120 (0.8%) • Más de 120 (1.0%)
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="overflow-x-auto w-full custom-scrollbar">
                        <table className="w-full text-xs border-collapse text-left min-w-max whitespace-nowrap">
                            <thead className="bg-slate-50/70 text-slate-400 border-b border-slate-100 uppercase text-[9px] font-black tracking-wider">
                                <tr>
                                    <th className="p-4 pl-6 w-16 text-center">Rango</th>
                                    <th className="p-4">Responsable</th>
                                    <th className="p-4 text-center text-emerald-700">Cobradas</th>
                                    <th className="p-4 text-center text-sky-700">Debitadas</th>
                                    <th className="p-4 text-center text-rose-600">Pendientes</th>
                                    <th className="p-4 text-center text-slate-500">Anuladas</th>
                                    <th className="p-4 text-center text-slate-800">Efectividad</th>
                                    <th className="p-4 text-right">Cobrado (ARS)</th>
                                    <th className="p-4 text-right">Cobrado (USD)</th>
                                    <th className="p-4 text-right text-rose-600">Pendiente (ARS)</th>
                                    <th className="p-4 text-right text-rose-600">Pendiente (USD)</th>
                                    <th className="p-4 text-right pr-6 text-amber-600">Incentivo Estimado</th>
                                </tr>
                            </thead>
                            <tbody className="font-semibold text-slate-700 divide-y divide-slate-100 bg-white">
                                {matrizCobradores.map((cob, index) => {
                                    const rank = index + 1;

                                    return (
                                        <tr 
                                            key={cob.nombre}
                                            onClick={() => {
                                                setSelectedCobrador(cob.nombre);
                                                setSubTab('COBRADAS');
                                            }}
                                            className="hover:bg-blue-50/30 cursor-pointer transition-colors duration-150 group"
                                        >
                                            <td className="p-4 pl-6 text-center">
                                                {rank === 1 && <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-50 border border-amber-200 text-amber-600 font-black text-[10px]">🥇</span>}
                                                {rank === 2 && <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-slate-100 border border-slate-200 text-slate-600 font-black text-[10px]">🥈</span>}
                                                {rank === 3 && <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-orange-50 border border-orange-200 text-orange-600 font-black text-[10px]">🥉</span>}
                                                {rank > 3 && <span className="text-[10px] font-mono text-slate-400 font-bold">#{rank}</span>}
                                            </td>
                                            <td className="p-4 text-slate-800 font-bold group-hover:text-blue-900 transition-colors uppercase">
                                                {cob.nombre}
                                            </td>
                                            <td className="p-4 text-center font-mono font-bold text-emerald-700">
                                                {cob.cuotasCobradas}
                                            </td>
                                            <td className="p-4 text-center font-mono font-bold text-sky-700">
                                                {cob.cuotasDebitadas}
                                            </td>
                                            <td className="p-4 text-center font-mono font-bold text-rose-600">
                                                {cob.cuotasPendientes}
                                            </td>
                                            <td className="p-4 text-center font-mono font-bold text-slate-500">
                                                {cob.cuotasAnuladas}
                                            </td>
                                            <td className="p-4 text-center font-mono font-black text-slate-800">
                                                <span className={`px-2 py-0.5 rounded-full text-[10px] ${
                                                    cob.efectividad >= 85 ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                                                    cob.efectividad >= 70 ? 'bg-amber-50 text-amber-700 border border-amber-200' :
                                                    'bg-rose-50 text-rose-700 border border-rose-200'
                                                }`}>
                                                    {cob.efectividad.toFixed(1)}%
                                                </span>
                                            </td>
                                            <td className="p-4 text-right font-mono font-bold text-emerald-600">
                                                {cob.cobradoARS > 0 ? formatCurrencyUniversal(cob.cobradoARS, '$') : '-'}
                                            </td>
                                            <td className="p-4 text-right font-mono font-bold text-blue-600">
                                                {cob.cobradoUSD > 0 ? formatCurrencyUniversal(cob.cobradoUSD, 'U$S') : '-'}
                                            </td>
                                            <td className="p-4 text-right font-mono font-bold text-rose-600">
                                                {cob.pendienteARS > 0 ? formatCurrencyUniversal(cob.pendienteARS, '$') : '-'}
                                            </td>
                                            <td className="p-4 text-right font-mono font-bold text-rose-600">
                                                {cob.pendienteUSD > 0 ? formatCurrencyUniversal(cob.pendienteUSD, 'U$S') : '-'}
                                            </td>
                                            <td className="p-4 text-right font-mono font-black text-amber-600 pr-6">
                                                <div className="flex flex-col items-end">
                                                    <span>{cob.incentivoARS > 0 ? formatCurrencyUniversal(cob.incentivoARS, '$') : '-'}</span>
                                                    <span className="text-[9px] text-slate-400 font-normal">({cob.pctIncentivo}%)</span>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : (

                /* ESCENARIO B: FICHA INDIVIDUAL */
                <div className="space-y-6 animate-fade-in">
                    
                    {/* ENCABEZADO Y RETORNO */}
                    <div className="bg-white p-4 px-6 rounded-2xl border border-slate-200 shadow-sm flex flex-col sm:flex-row justify-between sm:items-center gap-4">
                        <div className="flex items-center gap-4">
                            <button 
                                onClick={() => setSelectedCobrador('')}
                                className="group flex items-center gap-2 p-2.5 px-4 bg-slate-100 hover:bg-slate-200/80 text-slate-700 rounded-xl text-xs font-bold uppercase tracking-wider transition-all duration-200 active:scale-95"
                            >
                                <IconBack /> Volver al Resumen
                            </button>
                            <div className="border-l pl-4 border-slate-200">
                                <h3 className="text-md font-black text-[#01032d] uppercase tracking-tight">Ficha de Cobranza: {selectedCobrador}</h3>
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">
                                    Auditoría operativa • Efectividad global del {reporteIndividual.efectividad.toFixed(1)}%
                                </p>
                            </div>
                        </div>

                        <button
                            onClick={() => exportarExcelCobrador(subTab)}
                            className="flex items-center px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold text-xs uppercase tracking-wider transition-all duration-200 shadow-sm active:scale-95"
                        >
                            <IconDownload /> Exportar Vista Excel
                        </button>
                    </div>

                    {/* METRICAS DEL COBRADOR - CARDS (7 CARDS) */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                        
                        {/* 1. CUOTAS COBRADAS */}
                        <div 
                            onClick={() => setSubTab('COBRADAS')}
                            className={`p-4 border rounded-2xl shadow-sm flex flex-col justify-between cursor-pointer transition-all ${
                                subTab === 'COBRADAS' ? 'bg-emerald-50/50 border-emerald-300 ring-2 ring-emerald-500/20' : 'bg-white border-slate-200 hover:border-slate-300'
                            }`}
                        >
                            <p className="text-[9px] font-black text-emerald-700 uppercase tracking-widest">Cuotas Cobradas</p>
                            <h4 className="text-xl font-black text-emerald-700 mt-2 font-mono">{reporteIndividual.cuotasCobradas}</h4>
                            <p className="text-[9px] text-slate-400 mt-1 uppercase font-bold">Cobrada + Acreditada</p>
                        </div>

                        {/* 2. CUOTAS DEBITADAS */}
                        <div 
                            onClick={() => setSubTab('DEBITADAS')}
                            className={`p-4 border rounded-2xl shadow-sm flex flex-col justify-between cursor-pointer transition-all ${
                                subTab === 'DEBITADAS' ? 'bg-sky-50/50 border-sky-300 ring-2 ring-sky-500/20' : 'bg-white border-slate-200 hover:border-slate-300'
                            }`}
                        >
                            <p className="text-[9px] font-black text-sky-700 uppercase tracking-widest">Cuotas Debitadas</p>
                            <h4 className="text-xl font-black text-sky-700 mt-2 font-mono">{reporteIndividual.cuotasDebitadas}</h4>
                            <p className="text-[9px] text-slate-400 mt-1 uppercase font-bold">Estado DEBITADA</p>
                        </div>

                        {/* 3. CUOTAS PENDIENTES */}
                        <div 
                            onClick={() => setSubTab('PENDIENTES')}
                            className={`p-4 border rounded-2xl shadow-sm flex flex-col justify-between cursor-pointer transition-all ${
                                subTab === 'PENDIENTES' ? 'bg-rose-50/50 border-rose-300 ring-2 ring-rose-500/20' : 'bg-white border-rose-100 hover:border-rose-200'
                            }`}
                        >
                            <p className="text-[9px] font-black text-rose-600 uppercase tracking-widest">Cuotas Pendientes</p>
                            <h4 className="text-xl font-black text-rose-600 mt-2 font-mono">{reporteIndividual.cuotasPendientes}</h4>
                            <p className="text-[9px] text-slate-400 mt-1 uppercase font-bold">Por quién informó</p>
                        </div>

                        {/* 4. CUOTAS ANULADAS (NUEVA CARD) */}
                        <div 
                            onClick={() => setSubTab('ANULADAS')}
                            className={`p-4 border rounded-2xl shadow-sm flex flex-col justify-between cursor-pointer transition-all ${
                                subTab === 'ANULADAS' ? 'bg-slate-100 border-slate-400 ring-2 ring-slate-400/20' : 'bg-white border-slate-200 hover:border-slate-300'
                            }`}
                        >
                            <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Cuotas Anuladas</p>
                            <h4 className="text-xl font-black text-slate-700 mt-2 font-mono">{reporteIndividual.cuotasAnuladas}</h4>
                            <p className="text-[9px] text-slate-400 mt-1 uppercase font-bold">Por quién informó</p>
                        </div>

                        {/* 5. TOTAL PENDIENTE */}
                        <div className="bg-white p-4 border border-rose-100 rounded-2xl shadow-sm flex flex-col justify-between bg-rose-50/10">
                            <p className="text-[9px] font-black text-rose-600 uppercase tracking-widest">Total Pendiente</p>
                            <h4 className="text-md font-black text-rose-600 mt-2 font-mono">
                                {formatCurrencyUniversal(reporteIndividual.pendienteARS, '$')}
                            </h4>
                            {reporteIndividual.pendienteUSD > 0 && (
                                <h5 className="text-xs font-black text-blue-600 mt-0.5 font-mono">
                                    {formatCurrencyUniversal(reporteIndividual.pendienteUSD, 'U$S')}
                                </h5>
                            )}
                        </div>

                        {/* 6. EFECTIVIDAD DE COBRO */}
                        <div className="bg-white p-4 border border-slate-200 rounded-2xl shadow-sm flex flex-col justify-between">
                            <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Efectividad Cobro</p>
                            <h4 className="text-xl font-black text-[#01032d] mt-2 font-mono">
                                {reporteIndividual.efectividad.toFixed(1)}%
                            </h4>
                            <p className="text-[9px] text-slate-400 mt-1 uppercase font-bold">
                                (Cob+Deb) / Cartera Total
                            </p>
                        </div>

                        {/* 7. INCENTIVO VARIABLE POR VOLUMEN */}
                        <div className="bg-white p-4 border border-amber-200 rounded-2xl shadow-sm flex flex-col justify-between bg-amber-50/20">
                            <div className="flex justify-between items-center">
                                <p className="text-[9px] font-black text-amber-700 uppercase tracking-widest">Incentivo</p>
                                <span className="text-[9px] font-mono font-bold bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">
                                    {reporteIndividual.pctIncentivo}%
                                </span>
                            </div>
                            <h4 className="text-md font-black text-amber-600 mt-2 font-mono">
                                {formatCurrencyUniversal(reporteIndividual.incentivoARS, '$')}
                            </h4>
                            {reporteIndividual.incentivoUSD > 0 && (
                                <h5 className="text-xs font-black text-blue-600 mt-0.5 font-mono">
                                    {formatCurrencyUniversal(reporteIndividual.incentivoUSD, 'U$S')}
                                </h5>
                            )}
                        </div>
                    </div>

                    {/* CONTENEDOR DE SOLAPAS Y TABLA DINÁMICA */}
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
                        
                        <div className="p-3 px-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between flex-wrap gap-3">
                            <div className="flex bg-slate-200/60 p-1 rounded-xl gap-1">
                                <button
                                    onClick={() => setSubTab('COBRADAS')}
                                    className={`flex items-center gap-2 px-3 py-1.5 text-xs font-bold uppercase rounded-lg transition-all ${
                                        subTab === 'COBRADAS' ? 'bg-emerald-600 text-white shadow-sm font-black' : 'text-slate-600 hover:text-slate-900'
                                    }`}
                                >
                                    <IconFolder /> Cobradas ({reporteIndividual.itemsCobradas.length})
                                </button>

                                <button
                                    onClick={() => setSubTab('DEBITADAS')}
                                    className={`flex items-center gap-2 px-3 py-1.5 text-xs font-bold uppercase rounded-lg transition-all ${
                                        subTab === 'DEBITADAS' ? 'bg-sky-600 text-white shadow-sm font-black' : 'text-slate-600 hover:text-slate-900'
                                    }`}
                                >
                                    🏦 Debitadas ({reporteIndividual.itemsDebitadas.length})
                                </button>

                                <button
                                    onClick={() => setSubTab('PENDIENTES')}
                                    className={`flex items-center gap-2 px-3 py-1.5 text-xs font-bold uppercase rounded-lg transition-all ${
                                        subTab === 'PENDIENTES' ? 'bg-rose-500 text-white shadow-sm font-black' : 'text-rose-600 hover:bg-rose-50'
                                    }`}
                                >
                                    ⏳ Pendientes ({reporteIndividual.itemsPendientes.length})
                                </button>

                                <button
                                    onClick={() => setSubTab('ANULADAS')}
                                    className={`flex items-center gap-2 px-3 py-1.5 text-xs font-bold uppercase rounded-lg transition-all ${
                                        subTab === 'ANULADAS' ? 'bg-slate-700 text-white shadow-sm font-black' : 'text-slate-600 hover:bg-slate-200'
                                    }`}
                                >
                                    🚫 Anuladas ({reporteIndividual.itemsAnuladas.length})
                                </button>
                            </div>
                        </div>

                        {/* TABLA DINÁMICA SEGÚN SOLAPA */}
                        <div className="overflow-x-auto max-h-[460px] custom-scrollbar text-xs bg-white">
                            {subTab === 'COBRADAS' && (
                                <table className="w-full text-left font-medium border-collapse min-w-max whitespace-nowrap">
                                    <thead className="bg-slate-50 text-slate-400 font-black border-b uppercase text-[9px] tracking-wider sticky top-0 z-10 shadow-[0_1px_0_0_rgba(226,232,240,1)]">
                                        <tr>
                                            <th className="p-3 pl-4">Asegurado</th>
                                            <th className="p-3 text-center">Póliza</th>
                                            <th className="p-3 text-center">Compañía</th>
                                            <th className="p-3 text-center">Riesgo</th>
                                            <th className="p-3 text-center">Cuota</th>
                                            <th className="p-3 text-center">Endoso</th>
                                            <th className="p-3 text-center">N° Recibo</th>
                                            <th className="p-3 text-center">Estado</th>
                                            <th className="p-3 text-right">Importe</th>
                                            <th className="p-3 text-center">Forma Pago</th>
                                            <th className="p-3 text-center">Quién Informó</th>
                                            <th className="p-3 text-center">Quién Cobró</th>
                                            <th className="p-3 text-center">Fecha Cobro</th>
                                            <th className="p-3 text-right pr-6">Incentivo ({reporteIndividual.pctIncentivo}%)</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 text-slate-600 font-semibold">
                                        {reporteIndividual.itemsCobradas.length === 0 ? (
                                            <tr>
                                                <td colSpan="14" className="p-12 text-center text-slate-400 italic">
                                                    No se encontraron cuotas cobradas para este responsable.
                                                </td>
                                            </tr>
                                        ) : (
                                            reporteIndividual.itemsCobradas.map((item, i) => {
                                                const estado = String(item.estadoGestion || item.estado || '').trim().toUpperCase();
                                                const importe = parseCurrency(item.importe);
                                                const monedaRaw = String(item.moneda || item.monedaCobranza || item.monedaRefacturacion || '$').toUpperCase().trim();
                                                const esUSD = monedaRaw.includes('USD') || monedaRaw.includes('U$S') || monedaRaw.includes('DOLAR');
                                                const incentivoItem = (importe * reporteIndividual.pctIncentivo) / 100;

                                                return (
                                                    <tr key={item.id || i} className="hover:bg-slate-50/40 transition-colors">
                                                        <td className="p-3 pl-4 font-bold text-slate-800">{item.clienteNombre} {item.clienteApellido}</td>
                                                        <td className="p-3 font-mono font-bold text-slate-600 text-center">{item.nPoliza || '-'}</td>
                                                        <td className="p-3 uppercase text-center">{item.compania || item.companiaExpirante || '-'}</td>
                                                        <td className="p-3 uppercase text-center">{item.riesgo || '-'}</td>
                                                        <td className="p-3 text-center font-mono">{item.cuota || '-'}</td>
                                                        <td className="p-3 text-center font-mono">{item.endoso || '-'}</td>
                                                        <td className="p-3 text-center font-mono">{item.nroRecibo || '-'}</td>
                                                        <td className="p-3 text-center">
                                                            <span className="px-2 py-0.5 rounded-full text-[9px] font-black uppercase border bg-emerald-50 text-emerald-700 border-emerald-200">
                                                                {estado || '-'}
                                                            </span>
                                                        </td>
                                                        <td className="p-3 text-right font-mono font-bold text-slate-900">
                                                            {formatCurrencyUniversal(importe, esUSD ? 'U$S' : '$')}
                                                        </td>
                                                        <td className="p-3 text-center uppercase">{item.formaPago || '-'}</td>
                                                        <td className="p-3 text-center uppercase text-slate-500 font-bold">{item.quienInformo || '-'}</td>
                                                        <td className="p-3 text-center uppercase text-emerald-700 font-bold">{item.quienCobro || '-'}</td>
                                                        <td className="p-3 text-center font-mono text-[11px]">{item.fechaCobro || '-'}</td>
                                                        <td className="p-3 text-right pr-6 font-mono font-black text-amber-600">
                                                            {incentivoItem > 0 ? formatCurrencyUniversal(incentivoItem, esUSD ? 'U$S' : '$') : '-'}
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            )}

                            {subTab === 'DEBITADAS' && (
                                <table className="w-full text-left font-medium border-collapse min-w-max whitespace-nowrap">
                                    <thead className="bg-slate-50 text-slate-400 font-black border-b uppercase text-[9px] tracking-wider sticky top-0 z-10 shadow-[0_1px_0_0_rgba(226,232,240,1)]">
                                        <tr>
                                            <th className="p-3 pl-4">Asegurado</th>
                                            <th className="p-3 text-center">Póliza</th>
                                            <th className="p-3 text-center">Compañía</th>
                                            <th className="p-3 text-center">Riesgo</th>
                                            <th className="p-3 text-center">Cuota</th>
                                            <th className="p-3 text-center">Endoso</th>
                                            <th className="p-3 text-center">N° Recibo</th>
                                            <th className="p-3 text-center">Estado</th>
                                            <th className="p-3 text-right">Importe</th>
                                            <th className="p-3 text-center">Forma Pago</th>
                                            <th className="p-3 text-center">Quién Informó</th>
                                            <th className="p-3 text-center">Quién Cobró</th>
                                            <th className="p-3 text-center pr-6">Fecha Cobro</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 text-slate-600 font-semibold">
                                        {reporteIndividual.itemsDebitadas.length === 0 ? (
                                            <tr>
                                                <td colSpan="13" className="p-12 text-center text-slate-400 italic">
                                                    No se encontraron cuotas debitadas para este responsable.
                                                </td>
                                            </tr>
                                        ) : (
                                            reporteIndividual.itemsDebitadas.map((item, i) => {
                                                const importe = parseCurrency(item.importe);
                                                const monedaRaw = String(item.moneda || item.monedaCobranza || item.monedaRefacturacion || '$').toUpperCase().trim();
                                                const esUSD = monedaRaw.includes('USD') || monedaRaw.includes('U$S') || monedaRaw.includes('DOLAR');

                                                return (
                                                    <tr key={item.id || i} className="hover:bg-slate-50/40 transition-colors">
                                                        <td className="p-3 pl-4 font-bold text-slate-800">{item.clienteNombre} {item.clienteApellido}</td>
                                                        <td className="p-3 font-mono font-bold text-slate-600 text-center">{item.nPoliza || '-'}</td>
                                                        <td className="p-3 uppercase text-center">{item.compania || item.companiaExpirante || '-'}</td>
                                                        <td className="p-3 uppercase text-center">{item.riesgo || '-'}</td>
                                                        <td className="p-3 text-center font-mono">{item.cuota || '-'}</td>
                                                        <td className="p-3 text-center font-mono">{item.endoso || '-'}</td>
                                                        <td className="p-3 text-center font-mono">{item.nroRecibo || '-'}</td>
                                                        <td className="p-3 text-center">
                                                            <span className="px-2 py-0.5 rounded-full text-[9px] font-black uppercase border bg-sky-50 text-sky-700 border-sky-200">
                                                                DEBITADA
                                                            </span>
                                                        </td>
                                                        <td className="p-3 text-right font-mono font-bold text-slate-900">
                                                            {formatCurrencyUniversal(importe, esUSD ? 'U$S' : '$')}
                                                        </td>
                                                        <td className="p-3 text-center uppercase">{item.formaPago || '-'}</td>
                                                        <td className="p-3 text-center uppercase text-slate-500 font-bold">{item.quienInformo || '-'}</td>
                                                        <td className="p-3 text-center uppercase text-sky-700 font-bold">{item.quienCobro || '-'}</td>
                                                        <td className="p-3 text-center font-mono text-[11px] pr-6">{item.fechaCobro || '-'}</td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            )}

                            {subTab === 'PENDIENTES' && (
                                <table className="w-full text-left font-medium border-collapse min-w-max whitespace-nowrap">
                                    <thead className="bg-slate-50 text-slate-400 font-black border-b uppercase text-[9px] tracking-wider sticky top-0 z-10 shadow-[0_1px_0_0_rgba(226,232,240,1)]">
                                        <tr>
                                            <th className="p-3 pl-4">Asegurado</th>
                                            <th className="p-3 text-center">Póliza</th>
                                            <th className="p-3 text-center">Compañía</th>
                                            <th className="p-3 text-center">Riesgo</th>
                                            <th className="p-3 text-center">Cuota</th>
                                            <th className="p-3 text-center">Endoso</th>
                                            <th className="p-3 text-center">Estado Deuda</th>
                                            <th className="p-3 text-center">Intento Cobro</th>
                                            <th className="p-3 text-center">N° Recibo</th>
                                            <th className="p-3 text-center">Estado</th>
                                            <th className="p-3 text-right">Importe</th>
                                            <th className="p-3 text-center">Forma Pago</th>
                                            <th className="p-3 text-center">Quién Informó</th>
                                            <th className="p-3 text-center">Fecha Reclamo</th>
                                            <th className="p-3 text-center pr-6">Motivo Deuda</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 text-slate-600 font-semibold">
                                        {reporteIndividual.itemsPendientes.length === 0 ? (
                                            <tr>
                                                <td colSpan="15" className="p-12 text-center text-slate-400 italic">
                                                    No se encontraron cuotas pendientes para este responsable.
                                                </td>
                                            </tr>
                                        ) : (
                                            reporteIndividual.itemsPendientes.map((item, i) => {
                                                const estado = String(item.estadoGestion || item.estado || '').trim().toUpperCase();
                                                const importe = parseCurrency(item.importe);
                                                const monedaRaw = String(item.moneda || item.monedaCobranza || item.monedaRefacturacion || '$').toUpperCase().trim();
                                                const esUSD = monedaRaw.includes('USD') || monedaRaw.includes('U$S') || monedaRaw.includes('DOLAR');

                                                return (
                                                    <tr key={item.id || i} className="hover:bg-slate-50/40 transition-colors">
                                                        <td className="p-3 pl-4 font-bold text-slate-800">{item.clienteNombre} {item.clienteApellido}</td>
                                                        <td className="p-3 font-mono font-bold text-slate-600 text-center">{item.nPoliza || '-'}</td>
                                                        <td className="p-3 uppercase text-center">{item.compania || item.companiaExpirante || '-'}</td>
                                                        <td className="p-3 uppercase text-center">{item.riesgo || '-'}</td>
                                                        <td className="p-3 text-center font-mono">{item.cuota || '-'}</td>
                                                        <td className="p-3 text-center font-mono">{item.endoso || '-'}</td>
                                                        <td className="p-3 text-center uppercase text-slate-600 font-bold">{item.estadoDeuda || '-'}</td>
                                                        <td className="p-3 text-center font-mono">{item.intentoCobro !== undefined && item.intentoCobro !== null && item.intentoCobro !== '' ? item.intentoCobro : '-'}</td>
                                                        <td className="p-3 text-center font-mono">{item.nroRecibo || '-'}</td>
                                                        <td className="p-3 text-center">
                                                            <span className="px-2 py-0.5 rounded-full text-[9px] font-black uppercase border bg-rose-50 text-rose-700 border-rose-200">
                                                                {estado || '-'}
                                                            </span>
                                                        </td>
                                                        <td className="p-3 text-right font-mono font-bold text-slate-900">
                                                            {formatCurrencyUniversal(importe, esUSD ? 'U$S' : '$')}
                                                        </td>
                                                        <td className="p-3 text-center uppercase">{item.formaPago || '-'}</td>
                                                        <td className="p-3 text-center uppercase text-slate-500 font-bold">{item.quienInformo || '-'}</td>
                                                        <td className="p-3 text-center font-mono text-[11px]">{item.fechaReclamo || '-'}</td>
                                                        <td className="p-3 text-center uppercase pr-6 text-slate-500">{item.motivoDeuda || '-'}</td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            )}

                            {subTab === 'ANULADAS' && (
                                <table className="w-full text-left font-medium border-collapse min-w-max whitespace-nowrap">
                                    <thead className="bg-slate-50 text-slate-400 font-black border-b uppercase text-[9px] tracking-wider sticky top-0 z-10 shadow-[0_1px_0_0_rgba(226,232,240,1)]">
                                        <tr>
                                            <th className="p-3 pl-4">Asegurado</th>
                                            <th className="p-3 text-center">Póliza</th>
                                            <th className="p-3 text-center">Compañía</th>
                                            <th className="p-3 text-center">Riesgo</th>
                                            <th className="p-3 text-center">Cuota</th>
                                            <th className="p-3 text-center">Endoso</th>
                                            <th className="p-3 text-center">N° Recibo</th>
                                            <th className="p-3 text-center">Estado</th>
                                            <th className="p-3 text-right">Importe</th>
                                            <th className="p-3 text-center">Forma Pago</th>
                                            <th className="p-3 text-center">Quién Informó</th>
                                            <th className="p-3 text-center pr-6">Fecha Carga</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 text-slate-600 font-semibold">
                                        {reporteIndividual.itemsAnuladas.length === 0 ? (
                                            <tr>
                                                <td colSpan="12" className="p-12 text-center text-slate-400 italic">
                                                    No se encontraron cuotas anuladas para este responsable.
                                                </td>
                                            </tr>
                                        ) : (
                                            reporteIndividual.itemsAnuladas.map((item, i) => {
                                                const importe = parseCurrency(item.importe);
                                                const monedaRaw = String(item.moneda || item.monedaCobranza || item.monedaRefacturacion || '$').toUpperCase().trim();
                                                const esUSD = monedaRaw.includes('USD') || monedaRaw.includes('U$S') || monedaRaw.includes('DOLAR');

                                                return (
                                                    <tr key={item.id || i} className="hover:bg-slate-50/40 transition-colors">
                                                        <td className="p-3 pl-4 font-bold text-slate-800">{item.clienteNombre} {item.clienteApellido}</td>
                                                        <td className="p-3 font-mono font-bold text-slate-600 text-center">{item.nPoliza || '-'}</td>
                                                        <td className="p-3 uppercase text-center">{item.compania || item.companiaExpirante || '-'}</td>
                                                        <td className="p-3 uppercase text-center">{item.riesgo || '-'}</td>
                                                        <td className="p-3 text-center font-mono">{item.cuota || '-'}</td>
                                                        <td className="p-3 text-center font-mono">{item.endoso || '-'}</td>
                                                        <td className="p-3 text-center font-mono">{item.nroRecibo || '-'}</td>
                                                        <td className="p-3 text-center">
                                                            <span className="px-2 py-0.5 rounded-full text-[9px] font-black uppercase border bg-slate-100 text-slate-700 border-slate-300">
                                                                ANULADA
                                                            </span>
                                                        </td>
                                                        <td className="p-3 text-right font-mono font-bold text-slate-900">
                                                            {formatCurrencyUniversal(importe, esUSD ? 'U$S' : '$')}
                                                        </td>
                                                        <td className="p-3 text-center uppercase">{item.formaPago || '-'}</td>
                                                        <td className="p-3 text-center uppercase text-slate-500 font-bold">{item.quienInformo || '-'}</td>
                                                        <td className="p-3 text-center font-mono text-[11px] pr-6">{item.fechaCarga || '-'}</td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>

                </div>
            )}

            <style>{`
                .custom-scrollbar::-webkit-scrollbar { width: 5px; height: 5px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
                .animate-fade-in { animation: fadeIn 0.25s ease-out forwards; }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>
        </div>
    );
};

export default RendimientoCobradores;